import { Link } from "react-router-dom";

const Block = ({ n, title, children }) => (
  <section className="mt-12 first:mt-0">
    <div className="flex items-baseline gap-4">
      <span className="font-mono-x text-xs tracking-widest text-[var(--tha-red)] shrink-0">
        § {String(n).padStart(2, "0")}
      </span>
      <h2 className="font-display text-2xl sm:text-3xl text-white leading-tight">
        {title}
      </h2>
    </div>
    <div className="mt-4 space-y-3 text-sm text-[var(--tha-muted)] leading-relaxed">
      {children}
    </div>
  </section>
);

const Privacy = () => (
  <div data-testid="privacy-page" className="py-20">
    <div className="mx-auto max-w-3xl px-5 sm:px-8">
      <div className="flex items-center gap-3">
        <span className="h-px w-12 bg-[var(--tha-red)]" />
        <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
          AVG / GDPR — VERSIE 1.0
        </span>
      </div>
      <h1 className="font-display text-5xl sm:text-7xl mt-6 leading-none">
        PRIVACY <span className="neon-red-text">VERKLARING.</span>
      </h1>
      <p className="mt-6 text-sm text-[var(--tha-muted)] max-w-2xl">
        Laatste update: 13 februari 2026. Tik Hem Aan! (KvK 42065864) hecht
        groot belang aan de bescherming van jouw persoonsgegevens. In deze
        privacyverklaring leggen we helder uit welke gegevens wij verwerken,
        waarom we dat doen, hoe lang we ze bewaren en welke rechten je hebt.
      </p>

      <Block n={1} title="VERANTWOORDELIJKE">
        <p>
          Verantwoordelijke voor de verwerking van persoonsgegevens is:
          <br /><span className="text-white">Tik Hem Aan!</span> — eenmanszaak, Nederland
          <br />KvK 42065864
          <br />Contact: <a href="mailto:tikhemaannl@gmail.com" className="text-[var(--tha-red)] underline">tikhemaannl@gmail.com</a>
        </p>
      </Block>

      <Block n={2} title="WELKE GEGEVENS WIJ VERZAMELEN">
        <p>We verwerken de volgende categorieën persoonsgegevens:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><span className="text-white">Identiteitsgegevens</span>: voor- en achternaam</li>
          <li><span className="text-white">Contactgegevens</span>: e-mailadres, telefoonnummer</li>
          <li><span className="text-white">Adresgegevens</span>: straat, huisnummer, postcode, plaats</li>
          <li><span className="text-white">Bestelgegevens</span>: ordernummer, productinformatie, totaalbedrag, betaalstatus, Mollie payment ID</li>
          <li><span className="text-white">Communicatie</span>: berichten via contactformulier</li>
          <li><span className="text-white">Marketingvoorkeuren</span>: aanmelding/afmelding nieuwsbrief</li>
          <li><span className="text-white">Technische gegevens</span>: IP-adres, browsertype, besturingssysteem, bezochte pagina's (server logbestanden, max 14 dagen)</li>
        </ul>
        <p>
          We verzamelen géén bijzondere of gevoelige persoonsgegevens
          (etniciteit, religie, politieke voorkeur, gezondheidsgegevens
          enz.) en wij gebruiken geen gegevens van kinderen onder de 18 jaar.
        </p>
      </Block>

      <Block n={3} title="DOELEINDEN EN GRONDSLAGEN">
        <p>Wij verwerken jouw persoonsgegevens voor de volgende doeleinden:</p>
        <table className="w-full text-xs sm:text-sm mt-3 border-collapse">
          <thead>
            <tr className="text-left text-white border-b border-[var(--tha-border)]">
              <th className="py-2 pr-3 font-mono-x tracking-widest">DOEL</th>
              <th className="py-2 pr-3 font-mono-x tracking-widest">GRONDSLAG (AVG ART. 6)</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-[var(--tha-border)]">
              <td className="py-2 pr-3">Uitvoering bestelling en levering</td>
              <td className="py-2 pr-3">Sub b — uitvoering overeenkomst</td>
            </tr>
            <tr className="border-b border-[var(--tha-border)]">
              <td className="py-2 pr-3">Betalingsverwerking via Mollie</td>
              <td className="py-2 pr-3">Sub b — uitvoering overeenkomst</td>
            </tr>
            <tr className="border-b border-[var(--tha-border)]">
              <td className="py-2 pr-3">Fiscale en administratieve verplichtingen</td>
              <td className="py-2 pr-3">Sub c — wettelijke verplichting (art. 52 AWR)</td>
            </tr>
            <tr className="border-b border-[var(--tha-border)]">
              <td className="py-2 pr-3">Nieuwsbrief versturen</td>
              <td className="py-2 pr-3">Sub a — toestemming (afmelden mogelijk)</td>
            </tr>
            <tr className="border-b border-[var(--tha-border)]">
              <td className="py-2 pr-3">Klantenservice en klachten</td>
              <td className="py-2 pr-3">Sub b — uitvoering overeenkomst</td>
            </tr>
            <tr>
              <td className="py-2 pr-3">Fraudepreventie en site-beveiliging</td>
              <td className="py-2 pr-3">Sub f — gerechtvaardigd belang</td>
            </tr>
          </tbody>
        </table>
      </Block>

      <Block n={4} title="DELEN MET DERDEN (VERWERKERS)">
        <p>
          Wij delen jouw persoonsgegevens uitsluitend met partijen die
          noodzakelijk zijn voor de uitvoering van onze dienstverlening.
          Met alle verwerkers hebben we een verwerkersovereenkomst
          conform de AVG.
        </p>
        <ul className="list-disc pl-5 space-y-2 mt-3">
          <li>
            <span className="text-white">Mollie B.V.</span> (Amsterdam, NL) —
            betalingsverwerking. Door De Nederlandsche Bank gereguleerde
            betaalinstelling.{" "}
            <a
              href="https://www.mollie.com/nl/privacy"
              target="_blank"
              rel="noreferrer"
              className="text-[var(--tha-red)] underline"
            >
              privacybeleid Mollie
            </a>
          </li>
          <li>
            <span className="text-white">DHL Parcel Nederland</span> —
            bezorging van fysieke producten.
          </li>
          <li>
            <span className="text-white">Google Ireland Ltd. (Gmail SMTP)</span> —
            verzenden van transactionele e-mails en nieuwsbrief.
          </li>
          <li>
            <span className="text-white">Emergent / MongoDB Atlas</span> — hosting
            van de webshop en opslag van bestelgegevens (servers binnen de EU).
          </li>
        </ul>
        <p>
          We verkopen jouw persoonsgegevens nooit door aan derden voor
          marketingdoeleinden of profilering.
        </p>
      </Block>

      <Block n={5} title="DOORGIFTE BUITEN DE EER/EU">
        <p>
          Onze primaire verwerkers verwerken jouw gegevens binnen de EER.
          Voor incidentele doorgifte buiten de EER (bijvoorbeeld
          Google-infrastructuur) wordt gebruikgemaakt van de
          standaardcontractbepalingen van de Europese Commissie als
          passende waarborg.
        </p>
      </Block>

      <Block n={6} title="BEWAARTERMIJNEN">
        <ul className="list-disc pl-5 space-y-1">
          <li><span className="text-white">Bestelgegevens en facturen</span>: 7 jaar (fiscale bewaarplicht art. 52 AWR)</li>
          <li><span className="text-white">Klantaccount-gegevens</span>: tot 2 jaar na laatste activiteit</li>
          <li><span className="text-white">Nieuwsbriefabonnees</span>: tot afmelding</li>
          <li><span className="text-white">Contactberichten</span>: 2 jaar na ontvangst</li>
          <li><span className="text-white">Server-logs</span>: maximaal 14 dagen</li>
          <li><span className="text-white">Mollie betalingsdata</span>: zoals voorgeschreven door Mollie en de toezichthouder DNB</li>
        </ul>
      </Block>

      <Block n={7} title="BEVEILIGING">
        <p>
          We nemen passende technische en organisatorische maatregelen
          ter beveiliging van jouw gegevens, waaronder:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>versleutelde verbindingen (TLS/HTTPS) voor het gehele verkeer;</li>
          <li>betaalgegevens uitsluitend bij PCI-DSS-compliant Mollie, nooit bij ons;</li>
          <li>wachtwoordbeveiliging met bcrypt-hashing in de admin omgeving;</li>
          <li>JWT-tokens voor admin-sessies met korte levensduur;</li>
          <li>toegang tot gegevens beperkt tot personen die dit nodig hebben.</li>
        </ul>
        <p>
          Vermoed je een datalek of misbruik? Mail{" "}
          <a href="mailto:tikhemaannl@gmail.com" className="text-[var(--tha-red)] underline">
            tikhemaannl@gmail.com
          </a>
          . Wij melden datalekken indien wettelijk verplicht binnen
          72 uur aan de Autoriteit Persoonsgegevens.
        </p>
      </Block>

      <Block n={8} title="JOUW RECHTEN">
        <p>Onder de AVG heb je de volgende rechten:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><span className="text-white">Inzage</span>: opvragen welke gegevens we van je hebben</li>
          <li><span className="text-white">Rectificatie</span>: corrigeren van onjuiste gegevens</li>
          <li><span className="text-white">Verwijdering</span>: gegevens laten wissen (m.u.v. fiscale bewaarplicht)</li>
          <li><span className="text-white">Beperking</span>: tijdelijk stoppen van verwerking</li>
          <li><span className="text-white">Bezwaar</span>: tegen verwerking op grond van gerechtvaardigd belang</li>
          <li><span className="text-white">Dataportabiliteit</span>: ontvangen van een digitale kopie</li>
          <li><span className="text-white">Intrekking toestemming</span>: voor de nieuwsbrief (link in elke e-mail)</li>
        </ul>
        <p>
          Een verzoek kun je sturen naar{" "}
          <a href="mailto:tikhemaannl@gmail.com" className="text-[var(--tha-red)] underline">
            tikhemaannl@gmail.com
          </a>
          . Om misbruik te voorkomen kunnen we je vragen je identiteit te
          bevestigen. We reageren binnen 30 dagen.
        </p>
        <p>
          Ben je het niet eens met de afhandeling? Je hebt het recht een
          klacht in te dienen bij de{" "}
          <a
            href="https://www.autoriteitpersoonsgegevens.nl"
            target="_blank"
            rel="noreferrer"
            className="text-[var(--tha-red)] underline"
          >
            Autoriteit Persoonsgegevens
          </a>
          .
        </p>
      </Block>

      <Block n={9} title="COOKIES">
        <p>
          We gebruiken uitsluitend{" "}
          <span className="text-white">functionele cookies</span> en
          lokale opslag die strikt noodzakelijk zijn voor de werking
          van de webshop:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><code className="bg-[#0a0a0a] px-1.5 text-white">tha_age_ok_v1</code> — onthoudt de leeftijdscheck (localStorage, 1 jaar)</li>
          <li><code className="bg-[#0a0a0a] px-1.5 text-white">tha_cart_v1</code> — bewaart je winkelmandje (localStorage)</li>
          <li>Sessiecookies van Mollie en de webserver tijdens het afrekenen</li>
        </ul>
        <p>
          We gebruiken <span className="text-white">geen</span>{" "}
          analyse-, tracking- of advertentiecookies van derden. Daarom
          is een cookiebanner conform artikel 11.7a Telecommunicatiewet
          niet vereist.
        </p>
      </Block>

      <Block n={10} title="MINDERJARIGEN">
        <p>
          Tik Hem Aan! richt zich uitsluitend op meerderjarigen.
          Wij bewust verzamelen géén gegevens van personen jonger dan
          18 jaar. Als je vermoedt dat we onbedoeld gegevens van een
          minderjarige hebben verzameld, neem dan direct contact op,
          dan verwijderen we deze.
        </p>
      </Block>

      <Block n={11} title="WIJZIGINGEN">
        <p>
          Wij behouden ons het recht voor deze privacyverklaring
          aan te passen. Wijzigingen worden gepubliceerd op deze
          pagina met vermelding van de nieuwe versiedatum.
        </p>
      </Block>

      <div className="mt-16 pt-8 border-t border-[var(--tha-border)] text-xs font-mono-x tracking-widest text-[var(--tha-dim)] flex flex-wrap gap-4">
        <Link to="/voorwaarden" className="text-[var(--tha-red)] underline">
          ALGEMENE VOORWAARDEN
        </Link>
        <span>KVK 42065864</span>
        <span>NIX18</span>
      </div>
    </div>
  </div>
);

export default Privacy;
